import { Injectable } from '@angular/core';
import {
  ReconciliationManualFigure,
  ReconciliationManualFigureSource,
  ReconciliationManualFigureTemplateResponse,
  ReconciliationManualFigureUpsertRequest,
  ReconciliationManualFiguresGetListResponse,
} from '@ag-common-lib/public-api';
import { ReportingApiService } from './reporting-api.service';

/**
 * The figures the business records by hand on its weekly reconciliation sheet (TASKID-3629).
 *
 * Separate from ReconciliationApiService because these are written, not derived: this is the
 * independent source of truth the reconciliation report is checked against, and it is entered
 * against an ingestion period rather than read out of one.
 */
@Injectable()
export class ReconciliationManualFiguresApiService {
  private readonly basePath = '/api/reconciliation/manual-figures';

  constructor(private readonly reportingApiService: ReportingApiService) {}

  /** Everything recorded for a period, both measures, in display order. */
  getList(reportDate: string): Promise<ReconciliationManualFiguresGetListResponse> {
    return this.reportingApiService.request<ReconciliationManualFiguresGetListResponse>(
      `${this.basePath}/get-list`,
      { method: 'POST', body: { reportDate } },
    );
  }

  /**
   * The lines offered for entry for one measure.
   *
   * Describes what could be recorded rather than what has been, so the grid can open prefilled
   * with the lines the report shows instead of making the user assemble rows.
   */
  getTemplate(
    reportDate: string,
    source: ReconciliationManualFigureSource,
  ): Promise<ReconciliationManualFigureTemplateResponse> {
    return this.reportingApiService.request<ReconciliationManualFigureTemplateResponse>(
      `${this.basePath}/template`,
      { method: 'POST', body: { reportDate, source } },
    );
  }

  /** Records or replaces the figure for one line; the server decides which by its unique key. */
  upsert(request: ReconciliationManualFigureUpsertRequest): Promise<ReconciliationManualFigure> {
    return this.reportingApiService.request<ReconciliationManualFigure>(`${this.basePath}/upsert`, {
      method: 'POST',
      body: request,
    });
  }

  remove(id: number): Promise<void> {
    return this.reportingApiService.request<void>(`${this.basePath}/${id}`, { method: 'DELETE' });
  }
}
